import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Dialog from 'components/Dialog';
import { filter, pick } from 'lodash';
import { useForm, useTranslation } from 'hooks';
import DeleteDialogFooter from '../../DeleteDialogFooter';
import {
  CompanyInformation,
  Position,
  PositionReference,
  Responsibilities,
  WorkAbroad
} from './forms';
import { Form } from './style';

const components = {
  Footer: DeleteDialogFooter
};

const EMPTY_WORK_EXPERIENCE = {
  isActualEngagement: false
};

function Editor({
  syncErrors,
  syncing,
  workExperience,
  workExperienceId,
  groupID,
  companyId,
  onClose,
  onDelete,
  onSave,
  type
}) {
  const { t, tr } = useTranslation();
  const didMountRef = useRef(false);
  const location = useLocation();

  // Only render if this is a modal route (background state exists)
  const isModal = location.state?.background;

  const isOpen =
    isModal &&
    (location.pathname.includes('/work-experience') ||
      location.pathname.includes('/position-in-company') ||
      location.pathname.includes('/company'));

  const selectedWorkExperience = useMemo(
    () => workExperienceId && workExperience.find(item => item.id === workExperienceId),
    [workExperience, workExperienceId]
  );

  const selectedWorkExperienceCompany = useMemo(
    () =>
      !workExperienceId &&
      location.pathname.includes('/company/') &&
      workExperience.find(item => item.companyId === workExperienceId),
    [workExperience, workExperienceId, location]
  );

  const wExpInCompany = useMemo(
    () =>
      selectedWorkExperience &&
      selectedWorkExperience.companyId &&
      filter(workExperience, wp => wp.companyId === selectedWorkExperience.companyId).length,
    [workExperience, selectedWorkExperience]
  );

  // Get company information from the selected work experience
  const selectedCompany = useMemo(() => {
    if (selectedWorkExperience) {
      // If we have a work experience, get company info from it
      return pick(selectedWorkExperience, [
        'companyId',
        'companyName',
        'city',
        'country',
        'ownershipStructure',
        'companyEmployees',
        'companyTurnover',
        'industry'
      ]);
    } else if (companyId) {
      // If we have a companyId but no work experience, find the company
      const companyWorkExperience = workExperience.find(item => item.companyId === companyId);
      return companyWorkExperience
        ? pick(companyWorkExperience, [
            'companyId',
            'companyName',
            'city',
            'country',
            'ownershipStructure',
            'companyEmployees',
            'companyTurnover',
            'industry'
          ])
        : null;
    }
    return null;
  }, [selectedWorkExperience, companyId, workExperience]);

  const footerProps = useMemo(() => {
    if (selectedWorkExperience) {
      return {
        onDelete: () => onDelete(selectedWorkExperience),
        totalWexp: () => wExpInCompany
      };
    }
  }, [selectedWorkExperience, onDelete, wExpInCompany]);

  const activeWorkExperience = useMemo(() => {
    let result = selectedWorkExperience || EMPTY_WORK_EXPERIENCE;
    return selectedCompany ? { ...result, ...selectedCompany } : result;
  }, [selectedWorkExperience, selectedCompany]);

  activeWorkExperience.moduleToSave = type === 'CompanyInformation' ? 'Company' : 'Position';

  const {
    getValue,
    useInput,
    useTree,
    handleAdd,
    handleRemove,
    handleReset,
    handleSubmit
  } = useForm({
    initialValues: activeWorkExperience,
    validationSchema: type === 'CompanyInformation' ? 'companySchema' : 'workExperienceSchema',
    onSubmit: onSave
  });

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  useEffect(() => {
    if (didMountRef.current) {
      if (!syncErrors && !syncing) {
        handleClose();
      }
    } else {
      didMountRef.current = true;
    }
  }, [handleClose, syncErrors, syncing]);

  // Don't render if not a modal
  if (!isModal) return null;

  // === CompanyInformation modal ===
  if (type === 'CompanyInformation') {
    return (
      <Dialog
        actionLabel={t('button.save')}
        components={components}
        disabled={syncing}
        open={isOpen}
        syncing={syncing}
        title={tr('title.professionalExperience')}
        onAction={handleSubmit}
        onClose={handleClose}
        PaperProps={{
          ...{ loading: syncing }
        }}
      >
        <Form as={CompanyInformation} useInput={useInput} useTree={useTree} />
      </Dialog>
    );
  }

  // === PositionInCompany modal ===
  if (type === 'PositionInCompany') {
    return (
      <Dialog
        actionLabel={t('button.save')}
        components={components}
        disabled={syncing}
        footerProps={footerProps}
        open={isOpen}
        syncing={syncing}
        title={tr('title.professionalExperience')}
        onAction={handleSubmit}
        onClose={handleClose}
        PaperProps={{
          ...{ loading: syncing },
          sx: { maxWidth: '960px' }
        }}
      >
        <Form as={Position} getValue={getValue} useInput={useInput} useTree={useTree} />
        <Form as={Responsibilities} getValue={getValue} useInput={useInput} />
        <Form as={WorkAbroad} useInput={useInput} onAdd={handleAdd} onDelete={handleRemove} />
        <Form as={PositionReference} getValue={getValue} useInput={useInput} />
      </Dialog>
    );
  }

  // === WorkExperience modal ===
  return (
    <Dialog
      actionLabel={t('button.save')}
      components={components}
      disabled={syncing}
      footerProps={footerProps}
      open={isOpen}
      syncing={syncing}
      title={tr('title.professionalExperience')}
      onAction={handleSubmit}
      onClose={handleClose}
      PaperProps={{
        ...{ loading: syncing },
        sx: { maxWidth: '960px' }
      }}
    >
      {!workExperienceId && <Form as={CompanyInformation} useInput={useInput} useTree={useTree} />}
      <Form as={Position} getValue={getValue} useInput={useInput} useTree={useTree} />
      <Form as={Responsibilities} getValue={getValue} useInput={useInput} />
      <Form as={WorkAbroad} useInput={useInput} onAdd={handleAdd} onDelete={handleRemove} />
      <Form as={PositionReference} getValue={getValue} useInput={useInput} />
    </Dialog>
  );
}

Editor.propTypes = {
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  workExperience: PropTypes.array.isRequired,
  workExperienceId: PropTypes.string,
  companyId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  type: PropTypes.string
};

export default Editor;
